export interface TrackingAcquisition {
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_term?: string;
  utm_content?: string;
  click_ids?: Partial<Record<'gclid' | 'gbraid' | 'wbraid' | 'msclkid' | 'fbclid' | 'ttclid', string>>;
}

type TrackingStorage = Pick<Storage, 'getItem' | 'setItem'>;

const STORAGE_KEY = 'sw_acquisition_v1';
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content'] as const;
const CLICK_FIELDS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'ttclid'] as const;

function clean(value: string | null, max: number): string | undefined {
  const normalized = (value ?? '').replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return normalized ? normalized.slice(0, max) : undefined;
}

export function collectAcquisition(locationHref: string, referrer: string): TrackingAcquisition {
  let current: URL;
  try {
    current = new URL(locationHref);
  } catch {
    return {};
  }
  const result: TrackingAcquisition = {};
  const normalizedReferrer = clean(referrer, 2048);
  if (normalizedReferrer) {
    try {
      const referrerUrl = new URL(normalizedReferrer);
      if (referrerUrl.origin !== current.origin && ['http:', 'https:'].includes(referrerUrl.protocol)) {
        result.referrer = normalizedReferrer;
      }
    } catch {
      // Malformed referrers are not persisted by the widget.
    }
  }
  for (const field of UTM_FIELDS) {
    const value = clean(current.searchParams.get(field), field === 'utm_medium' ? 80 : 200);
    if (value) result[field] = value;
  }
  const clickIds: NonNullable<TrackingAcquisition['click_ids']> = {};
  for (const field of CLICK_FIELDS) {
    const value = clean(current.searchParams.get(field), 512);
    if (value) clickIds[field] = value;
  }
  if (Object.keys(clickIds).length > 0) result.click_ids = clickIds;
  return result;
}

export function resolveSessionAcquisition(
  locationHref: string,
  referrer: string,
  storage?: TrackingStorage,
): TrackingAcquisition {
  const current = collectAcquisition(locationHref, referrer);
  const target = storage ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
  if (Object.keys(current).length > 0) {
    try {
      target?.setItem(STORAGE_KEY, JSON.stringify(current));
    } catch {
      // Tracking remains best-effort when storage is unavailable.
    }
    return current;
  }
  try {
    const stored = target?.getItem(STORAGE_KEY);
    const decoded = stored ? JSON.parse(stored) : null;
    return decoded && typeof decoded === 'object' && !Array.isArray(decoded)
      ? decoded as TrackingAcquisition
      : {};
  } catch {
    return {};
  }
}
